
import React from "react";
import { FormLabel, FormToggle, FormElement, FormInput } from "../../../components/StyledElements";
import { useDataSets } from "contexts/DataSetsContext";
import { useDatasetTypes } from "../../../hooks/useDatasetTypes";


const selectStyles = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  fontFamily: "inherit",
  color: "var(--main-font-color)",
  backgroundColor: "var(--main-bg)",
  border: "1px solid var(--main-border-color)",
  borderRadius: "6px",
  outline: "none",
  boxSizing: "border-box",
  cursor: "pointer",
};

export const CoreDetails = ({ style = {}, onSelectTab = () => { } }) => {
  const { updatedDataSet, setUpdatedDataSet } = useDataSets();
  const { types, loading: typesLoading, error: typesError } = useDatasetTypes();

  const assignedType =
    updatedDataSet?.dataset_type ??
    (types ?? []).find(
      (type) =>
        type.public_id === updatedDataSet?.dataset_type_id || type.name === updatedDataSet?.type
    ) ??
    null;

  const selectedTypeId = assignedType?.public_id ?? "";

  // A stored free-text type with no matching definition (legacy datasets).
  const unmatchedType = !assignedType && updatedDataSet?.type ? updatedDataSet.type : null;

  // Selecting a type copies its schema and ingestion workflow onto the dataset; both stay
  // editable, and the dataset keeps them even if the type changes later.
  const applyType = (publicId) => {
    const type = (types ?? []).find((entry) => entry.public_id === publicId) ?? null;

    setUpdatedDataSet({
      ...updatedDataSet,
      // Clearing the selection must also clear the stored type name, otherwise the name-based
      // match below would immediately re-select the type that was just removed.
      dataset_type_id: type?.public_id ?? null,
      dataset_type: type ?? null,
      type: type?.name ?? "",
      // Selecting a type refreshes the item metadata fields (schema + ingestion workflow) from it.
      ...(type?.metadata_schema ? { metadata_schema: type.metadata_schema } : {}),
      ...(type?.ingestion_workflow ? { ingestion_workflow: type.ingestion_workflow } : {}),
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", ...style }}>

      <FormElement style={{ flexDirection: "column", display: "flex" }}>
        {
          updatedDataSet?.document_count ? (
            <FormLabel label={`${updatedDataSet.document_count} Items`} />
          ) : null}
        {
          updatedDataSet?.collection_chunk_count ? (
            <FormLabel label={`${updatedDataSet.collection_chunk_count} Chunked Items`} />
          ) : null}
      </FormElement>

      <FormElement style={{ flexDirection: "row", display: "flex", alignItems: "center", gap: "10px" }}>
        <FormLabel label={`Published (${updatedDataSet?.published ? "Yes" : "No"})`} style={{ margin: 0 }} />
        <FormToggle
          checked={updatedDataSet?.published ?? false}
          onChange={(e) => {
            updatedDataSet.published = e.target.checked;
            setUpdatedDataSet({ ...updatedDataSet });
          }}
        />
      </FormElement>
      <FormElement>
        <FormLabel label="Public ID" />
        <FormInput disabled type="text" value={updatedDataSet?.public_id ?? ""} />
      </FormElement>
      <FormElement>
        <FormLabel label="Chroma Collection Name" />
        <FormInput type="text" disabled={true} value={updatedDataSet?.chroma_collection_name ?? ""} />
      </FormElement>
      <FormElement>
        <FormLabel label="Name" />
        <FormInput type="text" placeholder="Enter dataset name" value={updatedDataSet?.name ?? ""}
          onChange={(e) => {
            setUpdatedDataSet({ ...updatedDataSet, name: e.target.value });
          }}
        />
      </FormElement>
      <FormElement style={{ flexDirection: "column", display: "flex" }}>
        <FormLabel label="Dataset type (optional)" />
        <select
          style={selectStyles}
          value={selectedTypeId}
          onChange={(event) => applyType(event.target.value)}
        >
          <option value="">
            {typesLoading ? "Loading dataset types…" : "No type (custom)"}
          </option>
          {(types ?? []).map((type) => (
            <option key={type.public_id} value={type.public_id}>
              {type.label ?? type.name} ({type.name})
            </option>
          ))}
        </select>
        <div style={{ fontSize: "12.5px", color: "#a0a6b1", marginTop: "4px" }}>
          {typesError
            ? typesError
            : assignedType?.description
              ? assignedType.description
              : "Optional. Choosing a type copies its item metadata schema and ingestion workflow onto this data set."}
        </div>
        {assignedType?.name ? (
          <div style={{ fontSize: "12px", color: "#a0a6b1", marginTop: "2px" }}>
            Type name: {assignedType.name}
            {updatedDataSet?.dataset_type_id ? ` · id: ${updatedDataSet.dataset_type_id}` : ""}
          </div>
        ) : null}
        {unmatchedType ? (
          <div style={{ fontSize: "12px", color: "#a0a6b1", marginTop: "2px" }}>
            Stored type: {unmatchedType} (no matching type definition)
          </div>
        ) : null}
      </FormElement>

      <FormElement>
        <FormLabel label="Description" />
        <FormInput
          as="textarea"
          rows={5}
          placeholder="Enter dataset description"
          style={{ resize: "vertical" }}
          value={updatedDataSet?.description ?? ""}
          onChange={(e) => {
            updatedDataSet.description = e.target.value;
            setUpdatedDataSet({ ...updatedDataSet });
          }}
        />
      </FormElement>
    </div>
  );
};

export default CoreDetails;
